import React from 'react';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
interface PageIntroProps {
  eyebrow?: string;
  title: string;
  /** The page's opening words, given a band of their own to be read in. */
  intro?: string;
  crumbs?: Crumb[];
  /** When supplied, the title sits over the photograph. */
  image?: string;
  /**
   * Empty when the photograph is decorative — which it is whenever the page
   * title already says what it shows.
   */
  imageAlt?: string;
  /** Facts, filters or actions that belong with the introduction. */
  children?: React.ReactNode;
}

/**
 * The head of every inner page: a photographic band carrying the
 * breadcrumb, eyebrow and the page's one `h1`, then a separate band for the
 * introduction.
 *
 * Splitting the two is deliberate — an introduction set over a photograph
 * is a contrast problem waiting to happen, and the words that explain a
 * page deserve to be read rather than decorated.
 */
export function PageIntro({
  eyebrow,
  title,
  intro,
  crumbs,
  image,
  imageAlt = '',
  children
}: PageIntroProps) {
  const hasIntroBand = Boolean(intro || children);
  return <>
      {image ? <div className="relative isolate bg-ink-deep" data-id="element-445" data-mp="1lukp1kv6v3kx">
          <img src={image} alt={imageAlt} loading="eager" decoding="sync" {...{
        fetchpriority: 'high'
      }} aria-hidden={imageAlt === '' ? true : undefined} className="absolute inset-0 -z-10 h-full w-full object-cover" data-id="element-446" data-mp="1qmvnejaa6hbd" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink-deep via-ink-deep/80 to-ink-deep/45" aria-hidden="true" data-id="element-447" data-mp="12ykt5yz3bfw2" />

          <div className="mx-auto flex min-h-[16rem] max-w-container flex-col justify-end px-gutter-sm py-10 sm:min-h-[20rem] sm:px-gutter-md sm:py-12 lg:px-gutter-lg" data-id="element-448" data-mp="1bxziuw7fnmfi">
            {crumbs && <div className="mb-5" data-id="element-449" data-mp="2nd4mc2fe1p06">
                <Breadcrumbs items={crumbs} onDark data-id="element-450" data-mp="2nccyjds18vkg" />
              </div>}
            {eyebrow && <p className="mb-3 border-l-4 border-flame pl-3 text-xs font-black uppercase tracking-[0.22em] text-flame sm:text-sm" data-id="element-451" data-mp="n9k70ajd06o0">
                {eyebrow}
              </p>}
            <h1 className="font-bebas text-5xl leading-[0.88] tracking-wide text-cream sm:text-6xl lg:text-7xl" data-id="element-452" data-mp="x8020olic02s">
              {title}
            </h1>
          </div>
        </div> : <div className="border-b border-hairline bg-sand" data-id="element-453" data-mp="4lp09am5zt4e">
          <div className="mx-auto max-w-container px-gutter-sm py-10 sm:px-gutter-md sm:py-12 lg:px-gutter-lg" data-id="element-454" data-mp="3vqygwbo9qv1c">
            {crumbs && <div className="mb-5" data-id="element-455" data-mp="ebi943ux9u4y">
                <Breadcrumbs items={crumbs} data-id="element-456" data-mp="ecjlcbnocmo9" />
              </div>}
            {eyebrow && <p className="mb-3 border-l-4 border-rust pl-3 text-xs font-black uppercase tracking-[0.22em] text-rust sm:text-sm" data-id="element-457" data-mp="9hx9669rqq34">
                {eyebrow}
              </p>}
            <h1 className="font-bebas text-5xl leading-[0.88] tracking-wide text-ink sm:text-6xl lg:text-7xl" data-id="element-458" data-mp="3cycqw801lyoo">
              {title}
            </h1>
          </div>
        </div>}

      {hasIntroBand && <div className="border-b border-hairline bg-cream" data-id="element-459" data-mp="1xazd9ccmbzv4">
          <div className="mx-auto max-w-container px-gutter-sm py-8 sm:px-gutter-md lg:px-gutter-lg" data-id="element-460" data-mp="1jjb5fl9ejxjy">
            {intro && <p className="max-w-prose text-lg leading-relaxed text-ink/80" data-id="element-461" data-mp="1ngz3gx6e66g2">
                {intro}
              </p>}
            {children}
          </div>
        </div>}
    </>;
}

/**
 * One content band. Every page uses this rather than repeating the
 * container and gutter classes, so the measure cannot drift page to page.
 */
export function Section({
  children,
  className = '',
  labelledBy
}: {
  children: React.ReactNode;
  className?: string;
  /** Id of the heading that names this section. */
  labelledBy?: string;
}) {
  return <section aria-labelledby={labelledBy} className={`mx-auto max-w-container px-gutter-sm py-12 sm:px-gutter-md sm:py-16 lg:px-gutter-lg ${className}`} data-id="element-462" data-mp="vtqlaz1iiu93">
      {children}
    </section>;
}